import React, { useState, useRef } from 'react';
import PortalOverlay from '@/components/PortalOverlay';
import { useI18n } from '@/contexts/I18nContext';
import { projectsAPI } from '@/services/api';
import { Camera, MessageSquare, Check, X, Loader2, Image as ImageIcon, Send } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '@/utils/compressImage';

const JobCompletionDialog = ({ job, onClose }) => {
  const { lang } = useI18n();
  const is = lang === 'is';
  const [activeAction, setActiveAction] = useState(null); // 'note' | 'photo'
  const [saving, setSaving] = useState(false);
  const [completedActions, setCompletedActions] = useState([]);

  // Note state
  const [note, setNote] = useState('');

  // Photo state
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  if (!job) return null;

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const existing = job.notes || '';
      const updated = existing ? `${existing}\n${note}` : note;
      await projectsAPI.updateJob(job.project_id, job.job_id, { notes: updated });
      toast.success(is ? 'Athugasemd vistuð' : 'Note saved');
      setCompletedActions(prev => [...prev, 'note']);
      setNote('');
      setActiveAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handlePickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = file.type.startsWith('image/') ? await compressImage(file) : file;
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('entity_type', 'job');
      formData.append('entity_id', job.job_id);
      await projectsAPI.uploadActivityImage(job.project_id, formData);
      setPhotos(prev => [...prev, URL.createObjectURL(compressed)]);
      toast.success(is ? 'Mynd vistuð' : 'Photo saved');
      if (!completedActions.includes('photo')) {
        setCompletedActions(prev => [...prev, 'photo']);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  return (
    <PortalOverlay open={true} onClose={onClose} placement="bottom" testId="job-completion-dialog">
      <div
        className="bg-card w-full sm:max-w-sm mx-auto rounded-t-2xl sm:rounded-2xl p-5 space-y-4 animate-in slide-in-from-bottom max-h-[90dvh] overflow-y-auto overscroll-contain"
      >
        {/* Header */}
        <div className="flex items-center gap-3" data-testid="job-completed-confirm">
          <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
            <Check size={20} className="text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{is ? 'Verk lokið!' : 'Task completed!'}</p>
            <p className="text-xs text-foreground-muted truncate">{job.title}</p>
          </div>
        </div>

        {/* Action buttons (always visible) */}
        {!activeAction && (
          <div className="space-y-2">
            <p className="text-xs text-foreground-muted">{is ? 'Viltu bæta einhverju við?' : 'Want to add anything?'}</p>

            <button
              onClick={() => setActiveAction('note')}
              className={`flex items-center gap-3 p-3 rounded-lg border w-full text-left transition-colors ${completedActions.includes('note') ? 'border-emerald-500/30 bg-emerald-500/5 opacity-60' : 'border-border hover:bg-card-hover'}`}
              data-testid="completion-add-note"
            >
              <MessageSquare size={16} className={completedActions.includes('note') ? 'text-emerald-400' : 'text-accent'} />
              <span className="text-sm flex-1">{is ? 'Bæta við athugasemd' : 'Add a note'}</span>
              {completedActions.includes('note') && <Check size={14} className="text-emerald-400" />}
            </button>

            {/* Photo: camera */}
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              className={`flex items-center gap-3 p-3 rounded-lg border w-full text-left transition-colors ${completedActions.includes('photo') ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border hover:bg-card-hover'}`}
              data-testid="completion-take-photo"
            >
              <Camera size={16} className={completedActions.includes('photo') ? 'text-emerald-400' : 'text-accent'} />
              <span className="text-sm flex-1">
                {uploading ? (is ? 'Hleð upp...' : 'Uploading...') : (is ? 'Taka mynd' : 'Take photo')}
              </span>
              {uploading && <Loader2 size={14} className="animate-spin" />}
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePickPhoto} />

            {/* Photo: gallery */}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className={`flex items-center gap-3 p-3 rounded-lg border w-full text-left transition-colors ${completedActions.includes('photo') ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border hover:bg-card-hover'}`}
              data-testid="completion-add-photo"
            >
              <ImageIcon size={16} className={completedActions.includes('photo') ? 'text-emerald-400' : 'text-accent'} />
              <span className="text-sm flex-1">
                {is ? 'Velja mynd úr safni' : 'Choose from gallery'}
                {photos.length > 0 && ` (${photos.length})`}
              </span>
              {completedActions.includes('photo') && !uploading && <Check size={14} className="text-emerald-400" />}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={handlePickPhoto} />

            {/* Photo thumbnails */}
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {photos.map((src, i) => (
                  <img key={i} src={src} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inline: Add Note */}
        {activeAction === 'note' && (
          <div className="space-y-3" data-testid="completion-note-form">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium">{is ? 'Athugasemd' : 'Note'}</p>
              <button onClick={() => setActiveAction(null)} className="text-foreground-muted p-1"><X size={14} /></button>
            </div>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={is ? 'Skrifaðu athugasemd...' : 'Write a note...'}
              rows={3}
              className="text-sm w-full"
              data-testid="completion-note-input"
            />
            <button onClick={handleSaveNote} disabled={saving || !note.trim()} className="btn btn-primary btn-sm w-full" data-testid="completion-save-note">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {is ? 'Vista' : 'Save'}
            </button>
          </div>
        )}

        {/* Close */}
        <button
          onClick={onClose}
          className="w-full btn btn-sm bg-card-hover text-foreground border border-border text-xs py-2.5"
          data-testid="completion-close"
        >
          {is ? 'Loka' : 'Close'}
        </button>
      </div>
    </PortalOverlay>
  );
};

export default JobCompletionDialog;
